import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { creatorApi } from '../../api/creatorApi';
import { socialPostApi } from '../../api/socialPostApi';
import { travelSafetyApi, newSafetyClientRequestId, safetyErrorMessage } from '../../api/travelSafetyApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import SafeImage from '../../components/SafeImage';
import { Button } from '../../components/ui';
import DirectMessageButton from '../messages/DirectMessageButton';
import { followersPath, followingPeoplePath } from '../creator-connections/creatorConnectionsModel';
import CreatorJourneyCard from './CreatorJourneyCard';
import CreatorPostCard from './CreatorPostCard';
import CreatorProfileEditDialog from './CreatorProfileEditDialog';
import { bumpFollowingFeedEpoch } from '../social-following/followingFeedRefresh';
import { normalizeCreatorJourney, normalizeCreatorPage, normalizeCreatorPost, normalizeCreatorProfile } from './creatorProfileModel';
import './creator.css';
import '../messages/travel-messages.css';

const pagesByCreator = new Map();
const emptyPage = () => ({ items: [], nextCursor: null, hasMore: false, loaded: false });
const cacheFor = (memberId) => pagesByCreator.get(memberId) || { posts: emptyPage(), journeys: emptyPage() };
export const clearCreatorPageCache = () => pagesByCreator.clear();

export default function CreatorPage({ tab }) {
  const { memberId } = useParams();
  const creatorId = memberId;
  const { t, tf } = useTravelTranslation();
  const { status: authStatus, isAuthenticated, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const generation = useRef(0);
  const contentGeneration = useRef(0);
  const [profile, setProfile] = useState(null);
  const [profilePhase, setProfilePhase] = useState('loading');
  const [profileError, setProfileError] = useState('');
  const [pages, setPages] = useState(() => cacheFor(creatorId));
  const [contentPhase, setContentPhase] = useState('idle');
  const [contentError, setContentError] = useState('');
  const [followBusy, setFollowBusy] = useState(false);
  const [followError, setFollowError] = useState('');
  const [confirmBlock, setConfirmBlock] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [blockError, setBlockError] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  const activeTab = tab === 'journeys' ? 'journeys' : 'posts';
  const currentPage = pages[activeTab];
  const ownProfile = isAuthenticated && String(user?.memberId || user?.id) === String(creatorId);

  // Me Hub「編輯個人資料」帶 ?edit=1 進入時自動開啟編輯對話框
  useEffect(() => {
    if (!ownProfile) return;
    if (searchParams.get('edit') === '1') {
      setEditOpen(true);
    }
  }, [ownProfile, searchParams]);

  function closeEditDialog() {
    setEditOpen(false);
    if (searchParams.get('edit') !== '1') return;
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }

  const loadProfile = useCallback(async () => {
    const requestGeneration = generation.current;
    setProfilePhase('loading'); setProfileError('');
    try {
      const value = normalizeCreatorProfile(await creatorApi.getProfile(creatorId));
      if (requestGeneration !== generation.current) return;
      setProfile(value); setProfilePhase('ready');
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      const notFound = cause?.raw?.key === 'travel.social.creator.not-found' || cause?.raw?.code === 96300;
      if (notFound) setProfilePhase('not-found'); else {
        setProfileError(cause?.message || t('travel.creator.profileLoadFailed'));
        setProfilePhase('error');
      }
    }
  }, [creatorId, t]);

  const loadContent = useCallback(async (kind, append = false) => {
    const requestGeneration = contentGeneration.current;
    const existing = cacheFor(creatorId)[kind];
    setContentPhase(append ? 'loading-more' : 'loading'); setContentError('');
    try {
      const payload = kind === 'posts'
        ? await creatorApi.listPosts(creatorId, { cursor: append ? existing.nextCursor : undefined, limit: 20 })
        : await creatorApi.listJourneys(creatorId, { cursor: append ? existing.nextCursor : undefined, limit: 20 });
      if (requestGeneration !== contentGeneration.current) return;
      const normalized = normalizeCreatorPage(payload, kind === 'posts' ? normalizeCreatorPost : normalizeCreatorJourney);
      const next = { ...cacheFor(creatorId), [kind]: { ...normalized, items: append ? [...existing.items, ...normalized.items] : normalized.items, loaded: true } };
      pagesByCreator.set(creatorId, next); setPages(next); setContentPhase('ready');
    } catch (cause) {
      if (requestGeneration !== contentGeneration.current) return;
      setContentError(cause?.message || t('travel.creator.contentLoadFailed')); setContentPhase('error');
    }
  }, [creatorId, t]);

  useEffect(() => {
    generation.current += 1; contentGeneration.current += 1; setProfile(null); setPages(cacheFor(creatorId)); setContentError('');
    if (!/^[1-9]\d*$/.test(creatorId || '')) { setProfilePhase('not-found'); return; }
    if (authStatus !== 'unknown') loadProfile();
  }, [creatorId, authStatus, loadProfile]);

  useEffect(() => {
    if (profilePhase !== 'ready') return;
    contentGeneration.current += 1;
    const cached = cacheFor(creatorId); setPages(cached);
    if (!cached[activeTab].loaded) loadContent(activeTab);
    else setContentPhase('ready');
  }, [activeTab, creatorId, profilePhase, loadContent]);

  async function toggleFollow() {
    if (!isAuthenticated) { navigate('/login', { state: { from: authReturnLocation(location) } }); return; }
    if (followBusy) return;
    const following = Boolean(profile.viewerFollowing);
    setFollowBusy(true); setFollowError('');
    try {
      await (following ? socialPostApi.unfollow(creatorId) : socialPostApi.follow(creatorId));
      bumpFollowingFeedEpoch();
      setProfile((current) => ({ ...current, viewerFollowing: !following,
        followerCount: Math.max(0, current.followerCount + (following ? -1 : 1)) }));
    } catch {
      setFollowError(following ? t('travel.creator.unfollowFailed') : t('travel.creator.followFailed'));
    }
    finally { setFollowBusy(false); }
  }

  async function handleProfileSaved() {
    clearCreatorPageCache();
    try {
      await refreshUser?.();
    } catch {
      /* nav will refresh on next bootstrap if needed */
    }
    await loadProfile();
  }

  function updatePostEngagement(postId, property, active) {
    const countKey = property === 'liked' ? 'likeCount' : 'saveCount';
    setPages((current) => {
      const posts = current.posts || emptyPage();
      const items = posts.items.map((row) => (String(row.postId) !== String(postId) ? row : ({
        ...row,
        engagement: {
          ...row.engagement,
          [property]: active,
          [countKey]: Math.max(0, (row.engagement?.[countKey] || 0) + (active ? 1 : -1)),
        },
      })));
      const next = { ...current, posts: { ...posts, items } };
      pagesByCreator.set(creatorId, next);
      return next;
    });
  }

  async function handleConfirmBlock() {
    if (blockBusy || !creatorId) return;
    setBlockBusy(true);
    setBlockError('');
    const requestId = newSafetyClientRequestId('block');
    try {
      await travelSafetyApi.blockMember(creatorId, requestId);
      setConfirmBlock(false);
      bumpFollowingFeedEpoch();
      clearCreatorPageCache();
      setProfilePhase('blocked');
    } catch (cause) {
      setBlockError(safetyErrorMessage(cause, t, 'travel.safety.blockFailed') || '封鎖失敗，請稍後再試。');
    } finally {
      setBlockBusy(false);
    }
  }

  if (authStatus === 'unknown' || profilePhase === 'loading') {
    return (
      <main className="creator-state" aria-live="polite">
        <div className="explore-spinner" />
        <p>{t('travel.creator.loading')}</p>
      </main>
    );
  }
  if (profilePhase === 'not-found') {
    return (
      <main className="creator-state">
        <h1>{t('travel.creator.unavailableTitle')}</h1>
        <p>{t('travel.creator.unavailableHint')}</p>
        <Link to="/explore">{t('travel.creator.backToExplore')}</Link>
      </main>
    );
  }
  if (profilePhase === 'blocked') {
    return (
      <main className="creator-state" role="status" data-testid="creator-blocked-state">
        <h1>已封鎖此使用者</h1>
        <p>您已封鎖該會員，不再顯示其個人檔案與貼文。</p>
        <Link to="/explore">{t('travel.creator.backToExplore')}</Link>
      </main>
    );
  }
  if (profilePhase === 'error') {
    return (
      <main className="creator-state" role="alert">
        <h1>{t('travel.creator.loadFailedTitle')}</h1>
        <p>{profileError}</p>
        <button type="button" onClick={loadProfile}>{t('travel.creator.tryAgain')}</button>
      </main>
    );
  }

  const followersTo = followersPath(creatorId);
  const followingTo = followingPeoplePath(creatorId);
  const emptyTitle = activeTab === 'posts' ? t('travel.creator.emptyPostsTitle') : t('travel.creator.emptyJourneysTitle');
  const emptyHint = activeTab === 'posts'
    ? (ownProfile ? t('travel.creator.emptyOwnPostsHint') : t('travel.creator.emptyPostsHint'))
    : t('travel.creator.emptyJourneysHint');
  return <main className="creator-page">
    <header className="creator-header">
      <SafeImage src={profile.avatarUrl} alt="" fallback={<span className="creator-header__avatar" aria-hidden="true">{profile.displayName.slice(0, 1).toUpperCase()}</span>} />
      <div className="creator-header__identity"><p>{t('travel.creator.eyebrow')}</p><h1>{profile.displayName}</h1>
        <dl>
          <div>
            <dt>{t('travel.creator.followersMetric')}</dt>
            <dd>
              {followersTo ? (
                <Link
                  to={followersTo}
                  className="creator-header__count-link"
                  aria-label={tf('travel.creator.followersCountAria', { count: profile.followerCount, name: profile.displayName })}
                >
                  {profile.followerCount}
                </Link>
              ) : profile.followerCount}
            </dd>
          </div>
          <div>
            <dt>{t('travel.creator.followingMetric')}</dt>
            <dd>
              {followingTo ? (
                <Link
                  to={followingTo}
                  className="creator-header__count-link"
                  aria-label={tf('travel.creator.followingCountAria', { count: profile.followingCount, name: profile.displayName })}
                >
                  {profile.followingCount}
                </Link>
              ) : profile.followingCount}
            </dd>
          </div>
          <div><dt>{t('travel.creator.postsMetric')}</dt><dd>{profile.publishedPostCount}</dd></div>
          <div><dt>{t('travel.creator.journeysMetric')}</dt><dd>{profile.publishedJourneyCount}</dd></div>
        </dl>
      </div>
      {ownProfile ? (
        <div className="creator-header__actions">
          <Button
            type="button"
            variant="secondary"
            className="creator-header__edit"
            data-testid="creator-edit-profile"
            onClick={() => setEditOpen(true)}
          >
            {t('travel.creator.editProfile')}
          </Button>
        </div>
      ) : (
        <div className="creator-header__actions">
          <button
            type="button"
            aria-pressed={Boolean(profile.viewerFollowing)}
            disabled={followBusy || blockBusy}
            onClick={toggleFollow}
          >
            {followBusy ? t('travel.creator.updating') : profile.viewerFollowing ? t('travel.creator.following') : t('travel.creator.follow')}
          </button>
          <DirectMessageButton peerMemberId={creatorId} label="傳訊息" />
          {isAuthenticated && creatorId ? (
            <button
              type="button"
              className="creator-header__block"
              data-testid="creator-block-btn"
              disabled={blockBusy}
              onClick={() => {
                setBlockError('');
                setConfirmBlock(true);
              }}
            >
              封鎖
            </button>
          ) : null}
        </div>
      )}
      {confirmBlock ? (
        <div
          className="creator-block-confirm"
          role="alertdialog"
          aria-labelledby="creator-block-confirm-title"
          data-testid="creator-block-confirm-dialog"
        >
          <p id="creator-block-confirm-title">確定要封鎖此使用者嗎？封鎖後將不再看到對方的內容與個人主頁。</p>
          <div className="creator-block-confirm__actions">
            <button
              type="button"
              data-testid="creator-block-confirm-btn"
              disabled={blockBusy}
              onClick={handleConfirmBlock}
            >
              {blockBusy ? '封鎖中...' : '確認封鎖'}
            </button>
            <button
              type="button"
              disabled={blockBusy}
              onClick={() => setConfirmBlock(false)}
            >
              取消
            </button>
          </div>
        </div>
      ) : null}
      {followError ? <p className="creator-header__error" role="alert">{followError}</p> : null}
      {blockError ? <p className="creator-header__error" role="alert" data-testid="creator-block-error">{blockError}</p> : null}
    </header>
    <nav className="creator-tabs" aria-label={t('travel.creator.tabsLabel')}>
      <NavLink end to={`/social/creators/${creatorId}`}>{t('travel.creator.postsTab')}</NavLink>
      <NavLink to={`/social/creators/${creatorId}/journeys`}>{t('travel.creator.journeysTab')}</NavLink>
    </nav>
    <section className="creator-content" aria-live="polite">
      {contentPhase === 'loading' ? (
        <div className="creator-content__state">
          <div className="explore-spinner" />
          <p>{activeTab === 'posts' ? t('travel.creator.loadingPosts') : t('travel.creator.loadingJourneys')}</p>
        </div>
      ) : null}
      {contentPhase === 'error' ? (
        <div className="creator-content__state" role="alert">
          <h2>{activeTab === 'posts' ? t('travel.creator.postsLoadFailed') : t('travel.creator.journeysLoadFailed')}</h2>
          <p>{contentError}</p>
          <button type="button" onClick={() => loadContent(activeTab)}>{t('travel.creator.tryAgain')}</button>
        </div>
      ) : null}
      {contentPhase === 'ready' && currentPage.items.length === 0 ? (
        <div className="creator-content__state">
          <h2>{emptyTitle}</h2>
          <p>{emptyHint}</p>
          {ownProfile && activeTab === 'posts' ? <Link to="/social/posts/new">發佈貼文</Link> : null}
          {!ownProfile ? <Link to="/explore">{t('travel.creator.backToExplore')}</Link> : null}
        </div>
      ) : null}
      <div className={`creator-${activeTab}-list`}>{currentPage.items.map((item) => activeTab === 'posts'
        ? <CreatorPostCard key={item.postId} post={item} onEngagementChange={updatePostEngagement} />
        : <CreatorJourneyCard key={item.publicationId} journey={item} />)}</div>
      {currentPage.hasMore ? (
        <button
          className="creator-content__more"
          type="button"
          disabled={contentPhase === 'loading-more'}
          onClick={() => loadContent(activeTab, true)}
        >
          {contentPhase === 'loading-more'
            ? t('travel.creator.loadingMore')
            : (activeTab === 'posts' ? t('travel.creator.loadMorePosts') : t('travel.creator.loadMoreJourneys'))}
        </button>
      ) : null}
      {currentPage.loaded && currentPage.items.length > 0 && !currentPage.hasMore ? (
        <p className="creator-content__end">{t('travel.creator.end')}</p>
      ) : null}
    </section>
    <CreatorProfileEditDialog
      open={editOpen}
      initialNickname={user?.nickname ?? ''}
      initialAvatarKey={user?.avatar ?? ''}
      initialAvatarUrl={profile.avatarUrl || ''}
      onClose={closeEditDialog}
      onSaved={handleProfileSaved}
    />
  </main>;
}
